import * as THREE from 'three';
import { COLORS } from '../../constants';
import { setLetterSpacing } from '../../lib/canvasText';

const FONT = "'Comfortaa', 'Helvetica Neue', Arial, sans-serif";

const drawIcon = (ctx, icon, c, r) => {
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'round';

  if (icon === 'sun') {
    ctx.lineWidth = r * 0.26;
    ctx.beginPath(); ctx.arc(c, c, r * 0.5, 0, Math.PI * 2); ctx.stroke();
    for (let i = 0; i < 8; i++) {
      const a = (i * Math.PI) / 4;
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a) * r * 0.85, c + Math.sin(a) * r * 0.85);
      ctx.lineTo(c + Math.cos(a) * r * 1.35, c + Math.sin(a) * r * 1.35);
      ctx.stroke();
    }
  } else if (icon === 'moon') {
    ctx.beginPath(); ctx.arc(c, c, r * 1.05, 0, Math.PI * 2); ctx.fill();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath(); ctx.arc(c + r * 0.55, c - r * 0.25, r * 0.9, 0, Math.PI * 2); ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  } else if (icon === 'hourglass') {
    ctx.lineWidth = r * 0.3;
    ctx.beginPath();
    ctx.moveTo(c - r, c - r * 1.1); ctx.lineTo(c + r, c - r * 1.1);
    ctx.lineTo(c - r, c + r * 1.1); ctx.lineTo(c + r, c + r * 1.1);
    ctx.closePath(); ctx.stroke();
  } else if (icon === 'eye') {
    ctx.beginPath();
    ctx.moveTo(c - r * 1.45, c);
    ctx.quadraticCurveTo(c, c - r * 1.55, c + r * 1.45, c);
    ctx.quadraticCurveTo(c, c + r * 1.55, c - r * 1.45, c);
    ctx.fill();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath(); ctx.arc(c, c, r * 0.55, 0, Math.PI * 2); ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    ctx.beginPath(); ctx.arc(c, c, r * 0.26, 0, Math.PI * 2); ctx.fill();
  } else if (icon === 'lips') {
    ctx.beginPath();
    ctx.moveTo(c - r * 1.5, c + r * 0.05);
    ctx.bezierCurveTo(c - r * 1.1, c - r * 0.95, c - r * 0.35, c - r * 0.9, c, c - r * 0.35);
    ctx.bezierCurveTo(c + r * 0.35, c - r * 0.9, c + r * 1.1, c - r * 0.95, c + r * 1.5, c + r * 0.05);
    ctx.bezierCurveTo(c + r * 0.9, c + r * 1.15, c - r * 0.9, c + r * 1.15, c - r * 1.5, c + r * 0.05);
    ctx.fill();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineWidth = r * 0.12;
    ctx.beginPath();
    ctx.moveTo(c - r * 1.5, c + r * 0.05);
    ctx.quadraticCurveTo(c, c + r * 0.4, c + r * 1.5, c + r * 0.05);
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  }
};

const toTexture = (canvas) => {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
};

export const makeLidTexture = (color, icon, size = 512) => {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, size, size);
  const iconLayer = document.createElement('canvas');
  iconLayer.width = iconLayer.height = size;
  drawIcon(iconLayer.getContext('2d'), icon, size / 2, size * 0.2);
  ctx.drawImage(iconLayer, 0, 0);
  return toTexture(canvas);
};

export const makeIconTexture = (icon, size = 256) => {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  drawIcon(canvas.getContext('2d'), icon, size / 2, size * 0.235);
  return toTexture(canvas);
};

export const makeLabelTexture = ([top, bottom], w = 2048, h = 200) => {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.strokeStyle = 'rgba(255,255,255,0.92)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  setLetterSpacing(ctx, '5px');
  ctx.font = `500 58px ${FONT}`;
  ctx.fillText(top, w / 2, h * 0.34);
  const width = Math.max(ctx.measureText(top).width, ctx.measureText(bottom).width);
  ctx.lineWidth = 3;
  ctx.beginPath(); ctx.moveTo(w / 2 - width / 2, h * 0.52); ctx.lineTo(w / 2 + width / 2, h * 0.52); ctx.stroke();
  ctx.fillText(bottom, w / 2, h * 0.72);
  ctx.save();
  ctx.translate(w * 0.675, h * 0.55);
  ctx.rotate(-Math.PI / 2);
  setLetterSpacing(ctx, '2px');
  ctx.font = `500 30px ${FONT}`;
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.fillText('10 ml ℮', 0, 0);
  ctx.restore();
  return toTexture(canvas);
};

export const makeGlowTexture = (size = 128) => {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.35)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return toTexture(canvas);
};

const brandGradient = (ctx, x, width) => {
  const g = ctx.createLinearGradient(x, 0, x + width, 0);
  g.addColorStop(0, COLORS.green);
  g.addColorStop(0.35, COLORS.pink);
  g.addColorStop(0.7, COLORS.orange);
  g.addColorStop(1, COLORS.blue);
  return g;
};

export const makeBaseTexture = (w = 2048, h = 440) => createBaseTexture(w, h).texture;

let logoPromise = null;
const loadLogo = () => logoPromise || (logoPromise = new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = '/images/beautykit-logo.png';
}));

export const createBaseTexture = (w = 2048, h = 440) => {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  const tex = toTexture(canvas);
  let logo = null;
  let text = { name: '', fn: '' };

  const draw = () => {
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, w, h);
    if (logo) {
      const lh = 200;
      ctx.drawImage(logo, 70, 36, (logo.width * lh) / logo.height, lh);
    }
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'right';
    setLetterSpacing(ctx, '8px');
    const line = [text.name ? text.name.toUpperCase() : '', text.fn].filter(Boolean).join('   ·   ');
    if (line) {
      const maxW = w - 90 - 660;
      let size = 64;
      ctx.font = `300 ${size}px ${FONT}`;
      while (ctx.measureText(line).width > maxW && size > 34) { size -= 2; ctx.font = `300 ${size}px ${FONT}`; }
      const lineW = ctx.measureText(line).width;
      ctx.fillStyle = brandGradient(ctx, w - 90 - lineW, lineW);
      ctx.fillText(line, w - 90, h - 100);
    }
    ctx.fillStyle = brandGradient(ctx, 0, w);
    ctx.globalAlpha = 0.5;
    ctx.fillRect(60, h - 22, w - 120, 4);
    ctx.globalAlpha = 1;
    tex.needsUpdate = true;
  };

  draw();
  loadLogo().then((img) => { logo = img; draw(); });
  return { texture: tex, setText: (name, fn) => { text = { name: name || '', fn: fn || '' }; draw(); } };
};
